import type { FormEvent } from 'react'
import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router'
import { safeNext, track } from '../lib/analytics'
import { Button } from '../ui'
import { api, ApiError, post } from '../lib/api'
import { useSession } from '../lib/session'
import styles from './LoginPage.module.css'

interface AuthConfig { otp: { length: number; ttl_minutes: number; resend_seconds: number } }
interface PlansInfo { promo?: { active: boolean } }

const POINTS: Array<[string, string]> = [
  ['Every NSE & BSE filing, as it lands', 'Classified by type and impact, with the PDF summarised.'],
  ['Alerts on Telegram and email', 'Only for the stocks and events you care about — quiet hours included.'],
  ['Watchlists, holdings and calendar', 'Import your portfolio and see results, dividends and board meetings ahead.'],
]

/** Full-screen sign-in / sign-up: email → 6-digit code. One flow for both; new emails get an account. */
export function LoginPage() {
  const { me, refresh } = useSession()
  const [params, setParams] = useSearchParams()
  const navigate = useNavigate()
  const next = safeNext(params.get('next')) ?? '/'
  const signup = params.get('signup') === '1'

  const [step, setStep] = useState<'email' | 'code'>('email')
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [cfg, setCfg] = useState<AuthConfig['otp']>({ length: 6, ttl_minutes: 10, resend_seconds: 60 })
  const [promo, setPromo] = useState(false)
  const [resendIn, setResendIn] = useState(0)
  const codeInput = useRef<HTMLInputElement>(null)

  useEffect(() => { document.title = signup ? 'Create your free account · BCEIE' : 'Sign in · BCEIE' }, [signup])
  useEffect(() => {
    api<AuthConfig>('/auth/config').then(c => setCfg(c.otp)).catch(() => undefined)
    api<PlansInfo>('/billing/plans').then(p => setPromo(!!p.promo?.active)).catch(() => undefined)
  }, [])
  // Signed in (already, or just now): continue to where the visitor was heading.
  useEffect(() => { if (me) navigate(next, { replace: true }) }, [me, next, navigate])
  useEffect(() => {
    if (resendIn <= 0) return
    const t = setTimeout(() => setResendIn(s => s - 1), 1000)
    return () => clearTimeout(t)
  }, [resendIn])
  useEffect(() => { if (step === 'code') codeInput.current?.focus() }, [step])

  const run = async (fn: () => Promise<void>) => {
    setBusy(true); setError(null)
    try { await fn() } catch (e) { setError(e instanceof ApiError ? e.message : 'Something went wrong. Try again.') } finally { setBusy(false) }
  }
  const sendCode = () => run(async () => {
    await post('/auth/otp/request', { email: email.trim() })
    track(signup ? 'signup-start' : 'signin-start')
    setStep('code'); setCode(''); setResendIn(cfg.resend_seconds)
  })
  const verify = (value: string) => run(async () => {
    await post('/auth/otp/verify', { email: email.trim(), code: value })
    track(signup ? 'signup-complete' : 'signin-complete')
    await refresh()
  })
  const onEmail = (e: FormEvent) => { e.preventDefault(); if (email.trim()) void sendCode() }
  const onCode = (e: FormEvent) => { e.preventDefault(); if (code.length === cfg.length) void verify(code) }
  const onCodeChange = (raw: string) => {
    const v = raw.replace(/\D/g, '').slice(0, cfg.length)
    setCode(v)
    if (v.length === cfg.length && !busy) void verify(v)
  }
  const switchMode = () => {
    const p = new URLSearchParams(params)
    if (signup) p.delete('signup'); else p.set('signup', '1')
    setParams(p, { replace: true })
  }

  if (me === undefined || me) {
    return <div className={styles.screen}><div className={styles.loading} role="status">Checking your session…</div></div>
  }

  return (
    <div className={styles.screen}>
      <aside className={styles.brandPanel}>
        <Link to="/" className={styles.brand} aria-label="BCEIE home">
          <span className={styles.mark} aria-hidden="true">B</span>
          <span className={styles.name}>BCEIE</span>
        </Link>
        <div className={styles.pitch}>
          <h2 className={styles.pitchTitle}>Corporate filings that move Indian stocks — without the noise.</h2>
          <ul className={styles.points}>
            {POINTS.map(([t, d]) => (
              <li key={t}><span className={styles.tick} aria-hidden="true">✓</span><div><b>{t}</b><span>{d}</span></div></li>
            ))}
          </ul>
        </div>
        <p className={styles.fine}>Information on corporate filings only. Not a recommendation to buy or sell.</p>
      </aside>

      <main className={styles.formPanel} id="main">
        <Link to="/" className={`${styles.brand} ${styles.brandMobile}`} aria-label="BCEIE home">
          <span className={styles.mark} aria-hidden="true">B</span>
          <span className={styles.nameDark}>BCEIE</span>
        </Link>
        <div className={styles.card}>
          {promo && <p className={styles.offer}>Launch offer — every feature free for now</p>}
          {step === 'email' ? (
            <>
              <h1 className={styles.title}>{signup ? 'Create your free account' : 'Welcome back'}</h1>
              <p className={styles.sub}>{signup ? 'Just your email — no password, no card.' : 'Sign in with a one-time code sent to your email.'}</p>
              <form onSubmit={onEmail} className={styles.form} noValidate>
                <label htmlFor="login-email" className={styles.label}>Email address</label>
                <input id="login-email" className={styles.input} type="email" autoComplete="email" autoFocus required
                  placeholder="you@example.com" value={email} onChange={e => setEmail(e.target.value)}
                  aria-describedby={error ? 'login-error' : undefined} aria-invalid={!!error} />
                {error && <p id="login-error" role="alert" className={styles.error}>{error}</p>}
                <Button type="submit" variant="primary" className={styles.submit} loading={busy} disabled={!email.trim()}>
                  {signup ? 'Create account' : 'Continue'}
                </Button>
              </form>
              <p className={styles.switch}>
                {signup ? 'Already have an account? ' : 'New to BCEIE? '}
                <button type="button" className={styles.linkBtn} onClick={switchMode}>{signup ? 'Sign in' : 'Create a free account'}</button>
              </p>
            </>
          ) : (
            <>
              <h1 className={styles.title}>Check your email</h1>
              <p className={styles.sub}>We sent a {cfg.length}-digit code to <b>{email.trim()}</b>. It expires in {cfg.ttl_minutes} minutes.</p>
              <form onSubmit={onCode} className={styles.form} noValidate>
                <label htmlFor="login-code" className={styles.label}>Sign-in code</label>
                <input id="login-code" ref={codeInput} className={`${styles.input} ${styles.code}`} inputMode="numeric" autoComplete="one-time-code"
                  pattern="\d*" maxLength={cfg.length} required placeholder={'•'.repeat(cfg.length)} value={code}
                  onChange={e => onCodeChange(e.target.value)} aria-describedby={error ? 'login-error' : 'login-code-help'} aria-invalid={!!error} />
                <p id="login-code-help" className={styles.help}>Can’t find it? Check spam or promotions.</p>
                {error && <p id="login-error" role="alert" className={styles.error}>{error}</p>}
                <Button type="submit" variant="primary" className={styles.submit} loading={busy} disabled={code.length !== cfg.length}>Sign in</Button>
              </form>
              <div className={styles.codeActions}>
                <button type="button" className={styles.linkBtn} disabled={resendIn > 0 || busy} onClick={() => void sendCode()}>
                  {resendIn > 0 ? `Resend code in ${resendIn}s` : 'Resend code'}
                </button>
                <button type="button" className={styles.linkBtn} onClick={() => { setStep('email'); setCode(''); setError(null) }}>Use a different email</button>
              </div>
            </>
          )}
          <p className={styles.legal}>By continuing you agree to the <a href="/legal/terms">Terms</a> and <a href="/legal/privacy">Privacy Policy</a>.</p>
        </div>
      </main>
    </div>
  )
}
